import React, { useState, useEffect } from "react"
import { Modal, Input, Avatar, Button, Spin, Switch } from "antd"
import { SearchOutlined, RobotOutlined, TeamOutlined } from "@ant-design/icons"
import { createGroup, GroupMember } from "firebase/groupsService"
import { API, Endpoints } from "utils/api"
import { getInitials } from "../utils"
import { Driver } from "../types"
import strings from "utils/strings"

interface CreateGroupModalProps {
	visible: boolean
	loggedUserId: string
	myName: string
	onClose: () => void
	onCreated: (newId: string) => void
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
	visible,
	loggedUserId,
	myName,
	onClose,
	onCreated,
}) => {
	const [newGroupName, setNewGroupName] = useState("")
	const [newGroupDesc, setNewGroupDesc] = useState("")
	const [selectedMembers, setSelectedMembers] = useState<GroupMember[]>([])
	const [driverSearch, setDriverSearch] = useState("")
	const [drivers, setDrivers] = useState<Driver[]>([])
	const [loadingDrivers, setLoadingDrivers] = useState(false)
	const [creating, setCreating] = useState(false)
	const [hasBotActive, setHasBotActive] = useState(false)

	useEffect(() => {
		if (!visible) return
		setNewGroupName("")
		setNewGroupDesc("")
		setSelectedMembers([{ id: loggedUserId, name: myName, type: "backoffice" }])
		setDriverSearch("")
		setHasBotActive(false)
		loadDrivers()
	}, [visible])

	const loadDrivers = async () => {
		setLoadingDrivers(true)
		try {
			const res = await API.get({ url: Endpoints.uriStaff() })
			if (res.ok) {
				const all: Driver[] = res.data?.results?.staff || []
				setDrivers(all.filter((s) => s.role === "driver" && s.isActive))
			}
		} finally {
			setLoadingDrivers(false)
		}
	}

	const toggleDriver = (driver: Driver) => {
		const exists = selectedMembers.find((m) => m.id === driver.firebaseUid)
		if (exists) {
			setSelectedMembers((prev) =>
				prev.filter((m) => m.id !== driver.firebaseUid)
			)
		} else {
			setSelectedMembers((prev) => [
				...prev,
				{ id: driver.firebaseUid, name: driver.name, type: "driver" },
			])
		}
	}

	const handleCreate = async () => {
		if (!newGroupName.trim()) return
		setCreating(true)
		try {
			const newId = await createGroup({
				name: newGroupName.trim(),
				description: newGroupDesc.trim(),
				hasBotActive,
				members: selectedMembers,
				createdById: loggedUserId,
				createdByName: myName,
			})
			onClose()
			onCreated(newId)
		} finally {
			setCreating(false)
		}
	}

	const filteredDrivers = drivers.filter(
		(d) =>
			d.name.toLowerCase().includes(driverSearch.toLowerCase()) ||
			d.email.toLowerCase().includes(driverSearch.toLowerCase())
	)

	const driverCount = selectedMembers.filter((m) => m.type === "driver").length

	return (
		<Modal
			title={
				<div className="modal-title">
					<TeamOutlined style={{ marginRight: 8, color: "#2B6CF4" }} />
					{strings.messaging.createNewGroup}
				</div>
			}
			visible={visible}
			onCancel={onClose}
			onOk={handleCreate}
			okText={strings.messaging.createGroup}
			cancelText={strings.generic.cancel}
			confirmLoading={creating}
			okButtonProps={{ disabled: !newGroupName.trim() || driverCount === 0 }}
			width={520}
			className="create-group-modal"
			centered
			bodyStyle={{
				padding: "16px 24px",
				maxHeight: "calc(100vh - 200px)",
				overflowY: "auto",
			}}
		>
			<div className="cg-section">
				<div className="cg-label">{strings.messaging.groupNameRequired}</div>
				<Input
					value={newGroupName}
					onChange={(e) => setNewGroupName(e.target.value)}
					placeholder={strings.messaging.groupNamePlaceholder}
					maxLength={60}
				/>
			</div>

			<div className="cg-section">
				<div className="cg-label">
					{strings.fields.description}{" "}
					<span className="cg-optional">{strings.messaging.optional}</span>
				</div>
				<Input
					value={newGroupDesc}
					onChange={(e) => setNewGroupDesc(e.target.value)}
					placeholder={strings.messaging.groupDescPlaceholder}
					maxLength={120}
				/>
			</div>

			<div className="cg-section">
				<div className="cg-bot-notice">
					<RobotOutlined style={{ color: "#F59E0B", fontSize: 16 }} />
					<span className="cg-bot-notice__text">
						<strong>Dingoo Bot</strong>
						<span className="cg-bot-notice__desc">
							{strings.messaging.botToggleDescription}
						</span>
					</span>
					<Switch
						checked={hasBotActive}
						onChange={setHasBotActive}
						size="small"
					/>
				</div>
			</div>

			<div className="cg-section">
				<div className="cg-label">
					{strings.messaging.driversRequired}
					{driverCount > 0 && (
						<span className="cg-count">
							{driverCount} {strings.messaging.selectedCount}
						</span>
					)}
				</div>
				<Input
					prefix={<SearchOutlined />}
					placeholder={strings.messaging.searchMember}
					value={driverSearch}
					onChange={(e) => setDriverSearch(e.target.value)}
					style={{ marginBottom: 6 }}
				/>
				<div className="driver-list">
					{loadingDrivers ? (
						<div className="driver-list__loading">
							<Spin size="small" />
						</div>
					) : filteredDrivers.length === 0 ? (
						<div className="driver-list__empty">
							{driverSearch
								? strings.generic.noResults
								: strings.messaging.noMemberFound}
						</div>
					) : (
						filteredDrivers.map((driver) => {
							const checked = Boolean(
								selectedMembers.find((m) => m.id === driver.firebaseUid)
							)
							return (
								<div
									key={driver.firebaseUid}
									className={`driver-row${checked ? " driver-row--checked" : ""}`}
									onClick={() => toggleDriver(driver)}
								>
									<div
										className={`driver-row__check${checked ? " driver-row__check--on" : ""}`}
									>
										{checked && <span>✓</span>}
									</div>
									<Avatar
										size={28}
										style={{
											background: "#2C3E50",
											fontSize: 11,
											flexShrink: 0,
										}}
									>
										{getInitials(driver.name)}
									</Avatar>
									<div className="driver-row__info">
										<span className="driver-row__name">{driver.name}</span>
										<span className="driver-row__email">{driver.email}</span>
									</div>
								</div>
							)
						})
					)}
				</div>
			</div>

			{driverCount > 0 && (
				<div className="cg-section" style={{ marginBottom: 0 }}>
					<div className="cg-label">{strings.messaging.selectedMembers}</div>
					<div className="selected-members">
						{selectedMembers.map((m) => (
							<div
								key={m.id}
								className={`selected-member-chip selected-member-chip--${m.type}`}
							>
								<Avatar
									size={18}
									style={{
										background: m.type === "backoffice" ? "#2B6CF4" : "#475569",
										fontSize: 8,
									}}
								>
									{getInitials(m.name)}
								</Avatar>
								<span>{m.name}</span>
								{m.id !== loggedUserId && (
									<span
										className="chip-remove"
										onClick={(e) => {
											e.stopPropagation()
											setSelectedMembers((prev) =>
												prev.filter((x) => x.id !== m.id)
											)
										}}
									>
										×
									</span>
								)}
							</div>
						))}
					</div>
				</div>
			)}
		</Modal>
	)
}
